import {
  setPendingMoveBackwards,
  setPendingMoveForwards,
  setShouldNavigateBackward,
  setShouldNavigateForward,
} from '../Lib/redux/slices/navigationControls';

export const generateUUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const deeplyEquals = (objectOne, objectTwo) =>
  // This function can deeply compare nested objects that don't contain functions
  JSON.stringify(objectOne) === JSON.stringify(objectTwo);

export const updateAndExposeDynamicHeightData = (dynamicWidgetHeightData) => {
  /* This function exposes the latest dynamicWidgetHeight data to the window object without
   * triggering re-renders. This is inside the sandboxed iframe.
   * Using the redux store is not recommended because this
   * data includes non-serialisable data, namely React refs.
   * (See https://redux.js.org/faq/actions#why-should-type-be-a-string-or-at-least-serializable-why-should-my-action-types-be-constants)
   * (To allow non-serializable values see: https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data)
   * Since the data gets provided by multiple components, Page, TYP, PageLayout, but only needs to be consumed and handled within
   * one self-managing component, DynamicWidgetHeight, no re-renders in other components are required.
   * This function ensures that by avoiding props-communication.
   */

  if (!window) {
    return;
  }

  if (!window.__private__) {
    //
    // Initialise Dynamic Height related data set
    //
    window.__private__ = {
      dynamicWidgetHeightData: {
        isDebugLoggingEnabled: false, // Turns debugging logic on which logs dynamic height related info to the console
        blockNavigation: false, // Toggle to briefly disable the back and continue CTA buttons whilst DynamicWidgetHeight recalibrates
        pendingNavigationInstruction: undefined, // Whilst the 'blockNavigation' field above is true, subsequent navigation attempts get captured here, in order to be reattempted once 'blockNavigation' toggles back to false
        timeStampOfBlockNavigationUpdate: undefined, // Navigation shall never be blocked for more than 2 seconds (ensured via logic in the useNavigation hook)
        headerRef: {},
        inputAreaRef: {},
        footerRef: {},
        thankYouPageContentRef: {},
        newsletterSignupContainerRef: {},
        isCurrentPageTheNewsLetterSignupPage: false,
        isLegalStatementMountingOrUnmounting: false,
        requiredInputAreaHeight: '',
        shouldResizeInputArea: false,
        shouldPostponeWidgetResize: false,
        isComplexWidgetResizeInProgress: false, // Reflects whether or not the on-going widget resize crosses the maximumWidgetHeight boundary or not
        hasDynamicHeightDomInitialisationStarted: false,
        hasTheUserNavigatedAtLeastOnce: false,
        isThereAStaticLoaderOnTheCurrentPage: false,
        resizeObservers: [],
        widgetHeightOverrides: {},
        failsafeAlgorithmData: {
          intervalId: undefined, // the failsafe algorithm is implemented as a 'setInterval' callback function - this is the memory reference for it
          iterationSkipCounter: 5, // polling frequency control variable (refer to the comment in the algorithm itself above this helper function 'updatePollingFrequencyControlVariables')
          isRequiredWidgetHeightReExaminationPending: false, // If true, this denotes that the next invocation of reExamineRequiredWidgetHeight() is imminent but some transient guard clauses are yet to update - refer to the helper function 'assessNecessityForWidgetHeightReExamination' in the algorithm itself
          timeStampOfLastUserInteraction: undefined, // timestamp [in ms since unix EPOCH] when the last user interaction with the form container was observed (latest click/tap)
          previousTimeSinceLastUserInteraction: undefined, // Describes the time [in ms] between the last invocation of the algorithm helper function 'updatePollingFrequencyControlVariables' and the last user interaction BEFORE that invocation
          totalIterationsToSkip: 5, // polling frequency control variable (refer to the comment in the algorithm itself above this helper function 'updatePollingFrequencyControlVariables')
          minimumIterationsToSkip: 5, // polling frequency control variable (refer to the comment in the algorithm itself above this helper function 'updatePollingFrequencyControlVariables')
          maximumIterationsToSkip: 200, // polling frequency control variable (refer to the comment in the algorithm itself above this helper function 'updatePollingFrequencyControlVariables')
          timeStampOfLastWidgetHeightReExamination: undefined, // timestamp [in ms since unix EPOCH] when the last invocation of reExamineRequiredWidgetHeight() occured
        },
      },
    };
  }

  //
  // Handle navigation block related side effect
  //
  const resumePendingNavigation = () => {
    const {
      pendingNavigationInstruction,
      isDebugLoggingEnabled,
      isThereAStaticLoaderOnTheCurrentPage,
    } = window?.__private__?.dynamicWidgetHeightData || {};

    switch (pendingNavigationInstruction) {
      case 'forward':
        if (isDebugLoggingEnabled) {
          console.log('DH: Resuming forward navigation');
        }
        // This setTimeout is needed to keep this code reliable on dropdown component pages which take longer to render and lead to races
        window.__private__.forwardNavTimeoutId = setTimeout(() => {
          updateAndExposeDynamicHeightData({
            pendingNavigationInstruction: undefined,
          });
          window.__private__.dispatchFunctionRef(
            setPendingMoveForwards({
              isAwaitingAsyncRequest: false,
            })
          );
          if (!isThereAStaticLoaderOnTheCurrentPage) {
            window.__private__.dispatchFunctionRef(
              setShouldNavigateForward({
                shouldNavigateForward: true,
              })
            );
          }
          clearTimeout(window.__private__.forwardNavTimeoutId);
        }, 0);
        break;
      case 'backward':
        if (isDebugLoggingEnabled) {
          console.log('DH: Resuming backward navigation');
        }
        window.__private__.backwardNavTimeoutId = setTimeout(() => {
          // This setTimeout is needed to keep this code reliable on dropdown component pages which take longer to render and lead to races
          updateAndExposeDynamicHeightData({
            pendingNavigationInstruction: undefined,
          });
          window.__private__.dispatchFunctionRef(
            setPendingMoveBackwards({
              isAwaitingAsyncRequest: false,
            })
          );
          if (!isThereAStaticLoaderOnTheCurrentPage) {
            window.__private__.dispatchFunctionRef(
              setShouldNavigateBackward({
                shouldNavigateBackward: true,
              })
            );
          }
          clearTimeout(window.__private__.backwardNavTimeoutId);
        }, 0);
        break;
      case undefined:
        break;
      default:
    }
  };

  const previousNavigationBlockValue =
    window.__private__?.dynamicWidgetHeightData?.blockNavigation;
  const nextNavigationBlockValue = dynamicWidgetHeightData?.blockNavigation;
  const isNavigationBlockAboutToClear =
    previousNavigationBlockValue === true && nextNavigationBlockValue === false;

  const hasComplexWidgetResizeBeenInProgress =
    window.__private__.dynamicWidgetHeightData?.isComplexWidgetResizeInProgress;
  const isComplexWidgetResizeCompleted =
    dynamicWidgetHeightData?.isComplexWidgetResizeInProgress === false;
  const hasComplexWidgetResizeJustCompleted =
    hasComplexWidgetResizeBeenInProgress === true &&
    isComplexWidgetResizeCompleted === true;
  const pendingNavigationInstruction =
    window.__private__.dynamicWidgetHeightData?.pendingNavigationInstruction;

  if (
    (isNavigationBlockAboutToClear && !hasComplexWidgetResizeBeenInProgress) ||
    (hasComplexWidgetResizeJustCompleted && pendingNavigationInstruction)
  ) {
    resumePendingNavigation();
  }

  //
  // Update Dynamic Height related data set
  //
  window.__private__.dynamicWidgetHeightData = {
    ...window.__private__.dynamicWidgetHeightData,
    ...dynamicWidgetHeightData,
  };
};

export const isFunctionLazyLoadable = ({ functionName, module }) => {
  /*
   * Returns true/false if the provided function name and module can/can't be loaded lazily
   */
  if (!module) {
    /* eslint-disable no-console */
    console.warn(
      'Lazy Loading failure - Module not found - Ensure that you moved the function into a module under `src/Helpers/lazyLoadable/` and that is gets exported from a module that is named identically to the functionName you have provided'
    );
    return false;
  }
  const moduleMembers = Object.keys(module);
  const isNumberOfModuleMembersOne = moduleMembers.length === 1;
  if (!isNumberOfModuleMembersOne) {
    /* eslint-disable no-console */
    console.warn(
      'Lazy Loading failure - Misconfigured module - Ensure that you provide a path to a module exporting a function that is actually lazy loadable by ensuring that the provided module contains no more than a single function export'
    );
    return false;
  }
  if (!module[functionName] || typeof module[functionName] !== 'function') {
    /* eslint-disable no-console */
    console.warn(
      'Lazy Loading failure - Function not found - Ensure that you provide a functionName argument matching the function name inside its exporting module'
    );
    return false;
  }
  return true;
};

/*
 * Generates an array of integers starting from the `start` value up to and including the `end` value.
 *
 * @param {number} start - The starting integer of the range.
 * @param {number} end - The ending integer of the range.
 * @returns {number[]} An array of consecutive integers from `start` to `end`, inclusive.
 *
 * Example:
 * range(3, 7)  // Returns [3, 4, 5, 6, 7]
 */
export const range = (start, end) => {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

/**
 * Generates a hexadecimal hash from a given string and seed.
 *
 * @param {string} str - The input string to be hashed.
 * @param {number} seedInteger - An arbitrary integer seed to vary the hash output.
 * @returns {string} A hexadecimal representation of the hash.
 *
 */
export const hashString = (str, seedInteger) => {
  let hash = seedInteger;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash).toString(16);
};

/**
 * Preloads an image by creating a new Image object and setting its `src` attribute.
 * This triggers the browser to fetch the image without adding it to the DOM.
 *
 * @param {string} url - The URL of the image to preload.
 */
export const preloadImage = (url) => {
  const img = new Image();
  img.src = url;
};

export const convertZeroToOneIndexIfNumber = (value) =>
  typeof value === 'number' ? value + 1 : value;

/**
 * Encodes a string to base64
 * @param {string} text - The text to encode
 * @returns {string} The encoded text
 */
export const encode64 = (text) => {
  return btoa(String.fromCharCode(...new TextEncoder().encode(text)));
};

/**
 * Decodes a base64 string
 * @param {string} text - The text to decode
 * @returns {string} The decoded text
 */
export const decode64 = (text) => {
  return new TextDecoder().decode(
    Uint8Array.from(atob(text), (c) => c.charCodeAt(0))
  );
};
